/**
 * Practice vs COM — the fully OFFLINE match. (§9.1 TRAINING, §10)
 *
 * Zero servers, zero sockets, zero lobby: this is `createWorld` +
 * `createPhysicsWorld` + `FixedStepDriver` at 60Hz running entirely in the tab.
 * The human drives ONE PlayerId through the same InputManager/buildInputCommand
 * path the online client uses; every other outfield slot on BOTH teams is
 * driven by `createTeamAI` (§10.1) at the picked difficulty; goalkeeper slots
 * are left null on purpose — sim/goalkeeper.ts is itself a complete keeper AI
 * and treats a null input as "AI keeper", a non-null one as human takeover
 * (verified: team-ai.ts skips `positions[id] === 'GK'`).
 *
 * Rendering reuses the EXACT pieces main.ts uses online — Renderer, CameraSystem,
 * mountHud — so practice and online can never drift visually. What practice does
 * NOT have: prediction/reconciliation/interpolation (nothing to reconcile
 * against — this world IS the authority).
 *
 * ── Local player switching (practice v1) ────────────────────────────────────
 * Tab-TAP takes over the teammate nearest the ball (never the GK); Tab-HOLD is
 * still the radar (§4.1). This deliberately does NOT import game/playerSwitch.ts
 * — that module is being rewired for the online path concurrently (SwitchPlayer
 * server honouring). Here a switch is just "which slot gets my InputCommand",
 * plus a 200ms smoothstep camera-target ease so the cut is not a snap. The
 * §4.8 cone-selection/auto-switch niceties are the online module's job; this is
 * the honest practice-grade version, and it is labelled as such.
 *
 * ── Pause is a REAL freeze ──────────────────────────────────────────────────
 * ESC stops the FixedStepDriver being advanced at all (no ticks, no input
 * consumption, no AI) and overlays Resume/Settings/Quit. The renderer keeps
 * presenting the frozen frame. On resume the driver's accumulator is reset so
 * the sim never "catches up" the paused wall-time. (Online matches cannot do
 * this — the server owns time; only practice gets a true pause.)
 */

import {
  FIXED_DT,
  GOAL_CELEBRATION_S,
  InputFlags,
  MAX_PLAYERS,
  MatchPhase,
  PASS_CHARGE_MAX_S,
  PASS_CHARGE_MIN_S,
  SHOT_CHARGE_MAX_S,
  SHOT_CHARGE_MIN_S,
  createMatchConfig,
  createPhysicsWorld,
  createStepResult,
  choosePassTarget,
  createWorld,
  isMatchOver,
  step,
  FixedStepDriver,
  type InputCommand,
  type MatchConfig,
  type PhysicsWorld,
  type WorldState,
  spawnSquads,
} from '@fg/shared/sim';
import { createTeamAI, type TeamAI } from '@fg/shared/ai';
import { InputManager, buildInputCommand, neutralContext, type InputContext } from '../input/InputManager.js';
import { CameraSystem, type CameraInput, type CameraPose } from '../render/camera.js';
import { Renderer } from '../render/renderer.js';
import { resolveBallHold } from './ballHold.js';
import { feedRefSignals } from '../render/refSignal.js';
import { createHudState, mountHud } from '../ui/hud.js';
import { buildFingerprint, formatFingerprint } from '@fg/shared/net';
import { mountBanners } from '../ui/banners.js';
import { configureMatchNames } from '../ui/playerNames.js';
import { previewCornerDelivery, type CornerDelivery } from '@fg/shared/ai';
import { createPredictedFlight } from '@fg/shared/sim';
import { createAudioSystem, type AudioTickInput } from './audio.js';
import { MATCH_ENVIRONMENT, applyTimeOfDay } from '../ui/environment.js';
import { deriveKit } from '../render/kit-colors.js';
import { mountMatchIntro, type MatchIntro } from '../ui/matchIntro.js';
import type { MatchSetup } from '../ui/menu.js';
import { mountPauseOverlay, type Overlay } from '../ui/pauseMenu.js';
import { mountPostMatch } from '../ui/postMatch.js';
import { playDoorsIntro, playGoalWipe } from '../ui/matchTransitions.js';
import { MatchStatsTracker, recordMatch, pickHeadlines } from './matchStats.js';
import { localStoragePbStore } from './challenges.js';
import { mountSettingsScreen, type SettingsScreen } from '../ui/settingsScreen.js';
import { applyInputFeel, qualityOverride, saveSettings, type GameSettings } from '../ui/settings.js';
import { possessionArrowVisible } from './possessionArrow.js';
import { applyCollectionAssist } from './collectionAssist.js';
import {
  applyCrosshairAim, cameraAimsWithCrosshair, crosshairAimYaw, crosshairArmed, crosshairShown,
} from './crosshairAim.js';
import {
  createReplaySystem,
  createReplayPlayerFrame,
  createReplayBallFrame,
  computeReplayCameraPose,
  type ReplayPlayerFrame,
  type ReplayBallFrame,
} from './replay.js';
import { TickInterpolator } from './tickInterp.js';
import { mountReplayChip } from '../ui/replayChip.js';

/** §5 middle-mouse CIRCULATE: the safe retain pass fires at a moderate charge —
 *  a firm-but-not-driven ball to a teammate, not a stab. */
const CIRCULATE_PASS_CHARGE = 0.5;
/** Tab: a TAP switches player, a HOLD opens the radar (§4.1 + §4.8). */
const TAB_TAP_MS = 200;
/** Camera-target ease on a player switch — the practice-v1 stand-in for §4.8's
 *  full blend (see header). */
const SWITCH_CAM_EASE_MS = 200;

/**
 * Goal-celebration instant replay (game/replay.ts). Slow-mo playback speed
 * and how many buffered seconds of build-up to show — sized so playback
 * naturally finishes right as the sim's own GOAL_CELEBRATION_S dwell ends
 * (real time ≈ sim time while the match isn't paused), which is what lets the
 * handback hide inside the following Kickoff staging cut per the brief. The
 * phase watch below is the actual handback trigger; this sizing is what makes
 * that handback land gracefully instead of visibly early/late.
 */
const REPLAY_PLAYBACK_SPEED = 0.5;
const REPLAY_WINDOW_S = GOAL_CELEBRATION_S * REPLAY_PLAYBACK_SPEED;
const REPLAY_HISTORY_S = 7;

/**
 * What the match was when it exited. Career and World Cup (ui/career.ts,
 * ui/worldCup.ts) need the REAL scoreline to advance a season, and this is the
 * only place every exit path funnels through — the pause menu's quit and the
 * full-time board's MENU button both call `quitToMenu`.
 *
 * `finished` distinguishes a match played to the final whistle from one the
 * player abandoned at 0-0 in the eighth minute. A competition must only record
 * the former; see main.ts's `onQuitToMenu`.
 *
 * Team A is ALWAYS the human's side in practice (spawnSquadsLocal seats them
 * there), so `scoreA` is the player's goals regardless of the fixture's venue.
 */
import { startHiddenTabPump } from '../render/hidden-tab-pump.js';

export interface PracticeResult {
  scoreA: number;
  scoreB: number;
  finished: boolean;
}

export interface PracticeOptions {
  /** Squad size / difficulty / half length / time of day / weather — the
   *  MATCH SETUP screen's output (ui/menu.ts). */
  setup: MatchSetup;
  settings: GameSettings;
  /** `result` is optional to IMPLEMENT (a handler that ignores it still type-
   *  checks), so every existing caller is unchanged. */
  onQuitToMenu(result: PracticeResult): void;
}

export interface PracticeHandle {
  dispose(): void;
}

// ---------------------------------------------------------------------------
// Kickoff spawn
// ---------------------------------------------------------------------------

/**
 * THE KICKOFF SPAWN MOVED to `spawnSquads` in shared/src/sim/roster.ts.
 *
 * This file used to carry a byte-identical copy of the server's ROSTERS /
 * SPAWN_ANCHORS / spawn loop, with a comment saying "if a roster ever lands in
 * MatchConfig, both copies die". It has (types.ts `MatchConfig.roster`), so they
 * did. Practice and the authoritative server now build their kickoff from one
 * function, which is also what makes the chosen XI below take the field with no
 * second implementation to keep in step.
 */

/** Nearest active, on-team, on-pitch OUTFIELD player to the ball. -1 = none. */
function pickNearestToBall(w: WorldState, team: number, excludeId: number): number {
  const p = w.players;
  let best = -1;
  let bestD = Infinity;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.team[i] !== team || p.sentOff[i] === 1) continue;
    if (i === excludeId) continue;
    if (w.positions[i] === 'GK') continue; // §4.5 human GK is its own mode — not via Tab
    const dx = p.px[i]! - w.ball.px;
    const dz = p.pz[i]! - w.ball.pz;
    const d = dx * dx + dz * dz;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

function smoothstep01(t: number): number {
  const x = t < 0 ? 0 : t > 1 ? 1 : t;
  return x * x * (3 - 2 * x);
}

// ---------------------------------------------------------------------------
// The mode
// ---------------------------------------------------------------------------

export async function startPractice(
  sceneEl: HTMLElement,
  uiEl: HTMLElement,
  opts: PracticeOptions,
): Promise<PracticeHandle> {
  const settings = opts.settings;
  const setup = opts.setup;

  // ── World (offline authority) ─────────────────────────────────────────────
  // Seed from the wall clock — legal here because it happens OUTSIDE the sim,
  // once, at construction (§15.3 bans clocks *inside* /sim, not at the edges).
  // Weather is REAL physics: wetPitch drives §3.2 friction/restitution in the
  // sim AND the rain/wet-sheen VFX (vfx.ts reads the same flag off the config).
  //
  // THE CHOSEN XI reaches the pitch here, and nowhere else. `setup.roster` is
  // built by ui/menu.ts from the squad screen's saved lineup (fg.squads.v1) when
  // Career or World Cup starts a fixture; it is UNDEFINED for a plain friendly,
  // and an undefined roster is exactly the kickoff this mode has always had
  // (sim/roster.ts `rosterSlotsFor`). Passing it through config rather than to
  // `spawnSquads` directly is deliberate: config is what a rewind copies, so
  // there is no second source of truth for who lines up where.
  const config: MatchConfig = createMatchConfig(setup.squad, {
    halfLengthS: setup.halfMinutes * 60,
    wetPitch: setup.weather === 'rain',
    seed: (Date.now() % 2146483646) + 1,
    // Spread conditionally, never `roster: setup.roster`. Under
    // `exactOptionalPropertyTypes` an explicit `undefined` is NOT the same as an
    // absent key, and "absent" is the compatibility case that has to reproduce
    // today's kickoff exactly.
    ...(setup.roster !== undefined ? { roster: setup.roster } : {}),
  });
  const world = createWorld(config);
  // §2 contact-timed impulses. Solo Practice (soloRules.ts) has run this path
  // since it landed; this is the OFFLINE practice match joining it.
  //
  // Measured 2026-08-02 with it off: the ball left at 7.87 m/s on the same tick
  // the pass animation started, with the nearer boot 0.58 m from it. The strike
  // animation then played out against a ball already 2.5 m away. Every pass and
  // shot read as a swing at nothing, which is what "the pass and kick look very
  // bad" actually was.
  //
  // Offline only — this is set at world build, never mid-match, and networked
  // play still builds its world elsewhere, so client and server cannot disagree.
  world.contactTiming = true;
  spawnSquads(world);

  const physics: PhysicsWorld = await createPhysicsWorld(config);
  physics.configurePitch(config);
  // physics-rapier.ts caller requirement: prime the capsules once before the
  // first step() or every first-tick sweep starts from the origin.
  physics.syncAndStep(world);

  // AI for BOTH teams (created after spawn so rosters are visible). Slots that
  // already hold a non-null input (the human) are left alone; GK slots are
  // never produced (goalkeeper.ts owns them).
  const difficulty = setup.difficulty;
  const aiA: TeamAI = createTeamAI(world, 0, difficulty);
  const aiB: TeamAI = createTeamAI(world, 1, difficulty);

  const inputs: Array<InputCommand | null> = new Array<InputCommand | null>(MAX_PLAYERS).fill(null);

  // CORNER LANDING TRAIL (render/cornerTrail.ts). Allocated ONCE — the predictor
  // writes into this buffer every frame a corner is being staged and the
  // renderer copies out of it, so the whole feature costs no per-frame garbage.
  const cornerDelivery: CornerDelivery = {
    show: false, taker: -1, exact: true, flight: createPredictedFlight(128),
  };
  const humanMask = new Uint8Array(MAX_PLAYERS); // §Restart no-teleport: this client's slot
  let lastPassTarget = -1; // §13 pass-target hysteresis: the currently-highlighted receiver
  /**
   * THE AIM (game/crosshairAim.ts): the world heading the screen-centre reticle
   * points along, refreshed from the LIVE camera pose each frame and read by the
   * sim callback below.
   *
   * NaN until the first camera update, so the very first tick of a mount aims by
   * `buildInputCommand`'s own result rather than by a pose that does not exist
   * yet. It is one frame old inside the tick (the rig updates in the render half
   * of the loop, the sim runs in the first half) — 16 ms of a smooth-damped,
   * acceleration-capped orbit, i.e. below the reticle's own 1-2 px line width at
   * any plausible mouse speed, and the alternative (reordering the loop so the
   * camera leads the sim) would change camera framing for every mode to buy
   * nothing visible.
   *
   * Refreshed ONLY from the live rig — never from the goal-replay orbit or the
   * intro flyby. Those poses look wherever the cinematic looks, and an aim that
   * followed them would leave the player pointing at a corner flag the moment
   * play resumed.
   */
  let crosshairYaw = Number.NaN;
  const stepOut = createStepResult();
  const driver = new FixedStepDriver();
  // Draws the frames BETWEEN sim ticks — see tickInterp.ts.
  const interp = new TickInterpolator();

  // ── Presentation (the SAME pieces the online path uses) ───────────────────
  const hud = createHudState();
  hud.nameA = 'YOU';
  hud.nameB = 'COM';
  hud.localTeam = 0;
  hud.pitchLength = config.pitchLength;
  hud.pitchWidth = config.pitchWidth;
  hud.halfLengthS = config.halfLengthS;
  // Practice is offline — there is no server build to compare against, so the HUD
  // shows the LOCAL fingerprint only (§12.6). The online path additionally sets
  // hud.serverFingerprint from the Welcome.
  hud.localFingerprint = formatFingerprint(buildFingerprint());
  const hudHandle = mountHud(uiEl, hud);
  // Broadcast event banners. Practice owns the authoritative StepResult, so
  // the banners get real events (scorer, card colour) — see banners.ts.
  // Name the cast for the broadcast overlays (ui/playerNames.ts). MUST run
  // before the banners mount: the restart caption reads this the first time a
  // phase flips, and an unconfigured roster silently degrades to "#7".
  configureMatchNames(config.squadSize);
  const banners = mountBanners(uiEl, hud);
  // Procedural gameplay audio (§11) — crowd bed/kicks/bounces/whistle. Volume
  // reads the settings blob on boot and live via the settings screen's hook.
  const audio = createAudioSystem();
  audio.setMasterVolume(settings.masterVolume);

  // Goal-celebration instant replay (game/replay.ts) — render-only; never
  // touches the sim. See the frame loop below for the record/playback wiring.
  const replay = createReplaySystem(MAX_PLAYERS, REPLAY_HISTORY_S);
  const replayChip = mountReplayChip(uiEl);
  const replayPlayers: ReplayPlayerFrame[] = Array.from({ length: MAX_PLAYERS }, () => createReplayPlayerFrame());
  const replayBall: ReplayBallFrame = createReplayBallFrame();
  let replayElapsedS = 0;
  let replayDurationS = 0;
  let replayGoalX = 0;
  let replayGoalZ = 0;
  // Crowd goal-roar (render/stadium.ts §6.6): counts down from GOAL_CELEBRATION_S
  // so the crowd's hyped animation speed decays back to ambient exactly as the
  // sim's own Goal-phase dwell ends, same sizing idea as REPLAY_WINDOW_S above.
  let crowdHypeRemainingS = 0;

  const renderer = new Renderer(sceneEl, qualityOverride(settings));
  renderer.setPitch(config);
  // Time of day → lighting rig (presentation only). Soft-fails if the shell's
  // seam is missing — see ui/environment.ts TODO(orchestrator).
  MATCH_ENVIRONMENT.weather = setup.weather;
  /*
   * timeOfDay was applied to the LIGHTING RIG below but never written back to
   * MATCH_ENVIRONMENT, which stayed on its 'day' default for the whole match.
   * Anything asking the shared record what time it is got the wrong answer —
   * the goal fireworks are night-only and could never have fired, whatever the
   * player picked on the setup screen.
   */
  MATCH_ENVIRONMENT.timeOfDay = setup.timeOfDay;
  if (!applyTimeOfDay(renderer, setup.timeOfDay) && setup.timeOfDay !== 'day') {
    console.warn('[fg] time-of-day preset not applied — lighting seam missing (ui/environment.ts)');
  }
  const onResize = (): void => renderer.resize();
  window.addEventListener('resize', onResize);

  const camera = new CameraSystem();
  camera.setMode(settings.camera.mode);
  camera.reset();

  const input = new InputManager(sceneEl, settings.controls);
  input.attach();
  applyInputFeel(input, settings); // recovered mouse/aim sensitivity (§7.1)
  const onSceneClick = (): void => {
    if (!paused && !ended) input.requestPointerLock();
    audio.resume(); // browsers block autoplay until a real user gesture (§11)
  };
  sceneEl.addEventListener('click', onSceneClick);

  // ── Control state ─────────────────────────────────────────────────────────
  let controlledId = pickNearestToBall(world, 0, -1);
  if (controlledId < 0) controlledId = 0; // 1v1 has no non-GK fallback issue; belt+braces
  let seq = 1;
  const ctx: InputContext = neutralContext();

  // Switch camera ease (see header).
  let easeT = 1;
  let easeFromX = 0;
  let easeFromY = 0;
  let easeFromZ = 0;

  const switchTo = (id: number): void => {
    if (id < 0 || id === controlledId) return;
    easeFromX = camTarget.x;
    easeFromY = camTarget.y;
    easeFromZ = camTarget.z;
    easeT = 0;
    controlledId = id;
  };

  // ── Lifecycle flags ───────────────────────────────────────────────────────
  let paused = false;
  let ended = false;
  let disposed = false;
  let pauseOverlay: Overlay | null = null;
  let fullTimeBoard: Overlay | null = null;
  let settingsScreen: SettingsScreen | null = null;

  // ── Kickoff intro (ui/matchIntro.ts) ──────────────────────────────────────
  // The sim is FROZEN during the flyby (a kickoff tableau), the camera follows
  // the intro's pose, and control hands over on finish/skip. The driver reset
  // + latch drain on done mean no paused wall-time is ever simulated and no
  // skip keypress leaks into the first live tick.
  const introPose: CameraPose = {
    x: 0, y: 18, z: 40, lookX: 0, lookY: 0, lookZ: 0, fov: 55,
    showBallArrow: false, ballArrowYaw: 0, offBallBlend: 0,
  };
  // Kit colour → CSS. deriveKit returns LINEAR triples (render convention);
  // DOM overlays need sRGB hex, so convert — reusing the linear values raw
  // renders every kit darker than the shirts on the pitch.
  const kitCss = (rgb: [number, number, number]): string => {
    const hex = rgb.map((c) => {
      const s = c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
      return Math.max(0, Math.min(255, Math.round(255 * s))).toString(16).padStart(2, '0');
    });
    return '#' + hex.join('');
  };
  const teamAName = setup.competition?.teamALabel ?? 'YOU';
  const teamBName = setup.competition?.teamBLabel ?? 'COM';

  // Ego stats (game/matchStats.ts) — the controlled player's run, folded into
  // the persistent career at full time and presented by the overlay below.
  const statsTracker = new MatchStatsTracker();

  // THE DOORS (ui/matchTransitions.ts, owner spec): team-colour doors slam
  // over the tableau, shake, and rip open onto the flyby already in motion.
  let doors: { dispose(): void } | null =
    playDoorsIntro(uiEl, teamAName, teamBName,
      kitCss(deriveKit(0).primary), kitCss(deriveKit(1).primary),
      () => { doors = null; },
      () => renderer.castReady);

  let intro: MatchIntro | null = mountMatchIntro(uiEl, {
    nameA: 'YOU',
    nameB: 'COM',
    pitchLength: config.pitchLength,
    pitchWidth: config.pitchWidth,
    // Cast loading starts the moment `renderer` is built, well before this
    // intro even mounts, so this is normally already true by the time the
    // 4.2s flyby finishes — it only holds the tableau a beat longer on a
    // slow device, rather than cutting to an empty pitch mid-load.
    isReady: () => renderer.castReady,
    onDone: (): void => {
      intro = null;
      driver.reset();
      input.state.takeOneShots(false);
      input.state.consumeMenu();
      input.state.consumeCameraCycle();
    },
  });

  // ── CAST LOADING SAFETY NET ────────────────────────────────────────────────
  // The doors' own gate (ui/matchTransitions.ts) only covers the pre-kickoff
  // cinematic — skip the intro, or pause mid-load, and the player lands on an
  // interactive scene while castPlayerVisual.ts is still streaming character
  // models in. That subsystem draws NOTHING until every model is ready, so
  // that is an empty pitch — visible right through the pause menu's own
  // translucent background (owner screenshot: MATCH PAUSED over a bare goal).
  // This sits above everything, pause included, independent of doors/intro
  // state, and only ever shows for as long as `renderer.castReady` is
  // actually false — usually 0ms, since cast loading started the moment
  // `renderer` was constructed a few lines up.
  let castOverlay: HTMLElement | null = null;
  let castPollTimer: ReturnType<typeof setInterval> | null = null;
  const clearCastOverlay = (): void => {
    if (castPollTimer) {
      clearInterval(castPollTimer);
      castPollTimer = null;
    }
    castOverlay?.remove();
    castOverlay = null;
  };
  if (!renderer.castReady) {
    castOverlay = document.createElement('div');
    castOverlay.style.cssText =
      'position:fixed;inset:0;z-index:9000;display:grid;place-items:center;' +
      'background:#0d1117;color:#e8f5ee;font-family:system-ui,sans-serif';
    castOverlay.innerHTML =
      `<div style="display:grid;place-items:center;gap:14px;text-align:center">` +
      `<div style="width:44px;height:44px;border:3px solid #2a3f33;border-top-color:#ffd94a;` +
      `border-radius:50%;animation:fg-cast-spin 900ms linear infinite"></div>` +
      `<div style="font-weight:800;font-size:17px;letter-spacing:.02em">Taking the pitch…</div>` +
      `<div style="font-size:13px;color:#9fb8a8;max-width:330px;line-height:1.5">Kitting out the last few players.</div>` +
      `</div>`;
    const castOverlayStyle = document.createElement('style');
    castOverlayStyle.textContent = '@keyframes fg-cast-spin{to{transform:rotate(360deg)}}';
    castOverlay.appendChild(castOverlayStyle);
    uiEl.appendChild(castOverlay);
    castPollTimer = setInterval(() => {
      if (renderer.castReady) clearCastOverlay();
    }, 100);
  }

  const openSettings = (): void => {
    if (settingsScreen) return;
    settingsScreen = mountSettingsScreen(uiEl, {
      settings,
      onChange: (s) => saveSettings(s),
      onClose: () => {
        settingsScreen?.dispose();
        settingsScreen = null;
      },
      live: { camera, renderer, input, audio },
    });
  };

  const pause = (): void => {
    if (paused || ended || disposed) return;
    paused = true;
    input.releasePointerLock();
    audio.setPaused(true); // §11 "mute the audio when a player pauses" — smooth duck
    pauseOverlay = mountPauseOverlay(uiEl, {
      onResume: resume,
      onSettings: openSettings,
      onQuitToMenu: quitToMenu,
    });
  };

  const resume = (): void => {
    if (!paused || disposed) return;
    paused = false;
    audio.setPaused(false); // smooth restore
    pauseOverlay?.dispose();
    pauseOverlay = null;
    settingsScreen?.dispose();
    settingsScreen = null;
    // Drop anything latched while the overlay was up (a stray P press must not
    // fire a punch on the first live tick) and forget paused wall-time.
    input.state.takeOneShots(false);
    driver.reset();
    input.requestPointerLock(); // the Resume click is the user gesture
  };

  const quitToMenu = (): void => {
    // Read the score BEFORE dispose() — the handle tears the world down.
    const result: PracticeResult = {
      scoreA: world.match.scoreA,
      scoreB: world.match.scoreB,
      finished: ended,
    };
    dispose();
    opts.onQuitToMenu(result);
  };

  const rematch = (): void => {
    dispose();
    // Fresh seed, same options — a whole new match.
    void startPractice(sceneEl, uiEl, opts);
  };

  // ESC while pointer-locked never reaches keydown (the browser eats it to exit
  // the lock), so lock-loss IS the pause signal during play. ESC when not
  // locked arrives normally and is consumed in the frame loop below.
  const onPointerLockChange = (): void => {
    if (document.pointerLockElement !== sceneEl && !paused && !ended && !disposed) pause();
  };
  document.addEventListener('pointerlockchange', onPointerLockChange);

  // ── Instant replay: skippable on any action key/click ─────────────────────
  // Capture-phase + stopImmediatePropagation, same pattern matchIntro.ts uses
  // for its own skip — the press must never leak into InputManager (which
  // would otherwise fire a shot/pass the instant control hands back) and
  // Escape is deliberately excluded so it keeps working as the pause key.
  const endReplay = (): void => {
    replay.endPlayback();
    replayChip.setVisible(false);
  };
  const onReplaySkipKey = (e: KeyboardEvent): void => {
    if (!replay.playing) return;
    if (e.code === 'Escape') return; // never eat pause
    e.preventDefault();
    e.stopImmediatePropagation();
    endReplay();
  };
  const onReplaySkipClick = (e: MouseEvent): void => {
    if (!replay.playing) return;
    e.stopImmediatePropagation();
    endReplay();
  };
  window.addEventListener('keydown', onReplaySkipKey, { capture: true });
  window.addEventListener('mousedown', onReplaySkipClick, { capture: true });

  // ── Frame loop (same shape as main.ts, minus netcode) ─────────────────────
  let lastFrameMs = performance.now();
  let elapsedS = 0;
  let fps = 60;
  let tabDownAtMs = -1;
  let rafPending = false;

  const scheduleFrame = (): void => {
    if (rafPending || disposed) return;
    rafPending = true;
    requestAnimationFrame((t) => {
      rafPending = false;
      frame(t);
    });
  };

  const frame = (nowMs: number): void => {
    if (disposed) return;
    // Reschedule FIRST, unconditionally — exactly as main.ts, soloPractice.ts
    // and testArena.ts do. The wrapper in scheduleFrame() has already cleared
    // rafPending by the time we get here, so any early return below without a
    // reschedule does not skip one frame: it permanently kills the rAF chain.
    // That is what the coalescing guard used to do. `lastFrameMs` is seeded from
    // performance.now() at loop setup, so whenever the first rAF happened to
    // arrive within the coalescing window of that seed the match died on frame
    // one — black screen, 0 fps, no error, intro stuck on screen forever. It
    // reproduced on every run in headless and intermittently on fast machines.
    scheduleFrame();
    // Coalesce: the hidden-tab pump and rAF can both aim at the same frame.
    if (nowMs - lastFrameMs < 4) return;

    const dtMs = Math.min(nowMs - lastFrameMs, 250);
    lastFrameMs = nowMs;
    const dtS = dtMs / 1000;
    fps += (1000 / Math.max(dtMs, 1) - fps) * 0.1;

    const p = world.players;
    const map = input.controlMap; // re-read: settings can rebind live
    const introActive = intro !== null && intro.active;

    if (!paused && !ended) elapsedS += dtS;

    // Ego stats: feed the controlled player's position each live frame. The
    // tracker's own teleport guard handles takeover switches and restarts.
    if (!paused && !ended && !introActive) {
      statsTracker.onTick(dtS, p.px[controlledId]!, p.pz[controlledId]!);
    }

    // Crowd goal-roar decay — real time, independent of the replay branch (the
    // crowd should keep easing back down even while a goal replay is on screen).
    // A real pause freezes it too, same as everything else here.
    if (!paused && crowdHypeRemainingS > 0) {
      crowdHypeRemainingS -= dtS;
      if (crowdHypeRemainingS <= 0) {
        crowdHypeRemainingS = 0;
        renderer.setCrowdExcitement(0);
      }
    }

    // Crowd anticipation (§6.6): the stands nearest the ball lean in as it
    // approaches a goal. Localized — only the end being attacked reacts, via
    // the bowl's sector-based exciteAt; the crowd system decays its own targets,
    // so feeding this every frame is the intended usage, not a leak. Suppressed
    // during the goal-roar window so the roar's full-bowl surge isn't diluted.
    if (!paused && !ended && crowdHypeRemainingS <= 0) {
      const bx = world.ball.px;
      const bz = world.ball.pz;
      const distToGoalLine = world.config.pitchLength * 0.5 - Math.abs(bx);
      if (distToGoalLine < 28 && Math.abs(bz) < 30) {
        renderer.exciteCrowdAt(bx, bz, 0.55 * (1 - distToGoalLine / 28));
      }
    }

    if (!paused && !ended && !introActive) {
      if (input.state.consumeMenu()) {
        pause();
      } else {
        // ── Tab: tap = takeover nearest-to-ball (practice switch), hold = radar
        const tabHeld = input.state.isRadarHeld(map);
        if (tabHeld && tabDownAtMs < 0) tabDownAtMs = nowMs;
        if (!tabHeld && tabDownAtMs >= 0) {
          if (nowMs - tabDownAtMs < TAB_TAP_MS) {
            switchTo(pickNearestToBall(world, 0, controlledId));
          }
          tabDownAtMs = -1;
        }
        hudHandle.setRadarExpanded(tabHeld);
        if (input.state.consumeCameraCycle()) {
          settings.camera.mode = camera.cycleMode();
          saveSettings(settings);
        }

        // ── Fixed-timestep sim (§3.1) — this world IS the authority ────────
        driver.advance(dtS, () => {
          interp.capture(world); // the state this tick starts FROM
          ctx.possessing = p.possessing[controlledId] === 1;
          ctx.speed = Math.hypot(p.vx[controlledId]!, p.vz[controlledId]!);
          camera.movementBasis(ctx);
          const cmd = buildInputCommand(
            input.state,
            map,
            ctx,
            seq++,
            world.tick + 1,
            FIXED_DT * 1000,
          );
          // §4 loose-ball collection assist — steers the input toward a collectable
          // loose ball (shared client seam; no sim/possession change).
          applyCollectionAssist(world, controlledId, cmd);
          // THE CROSSHAIR IS THE AIM (owner: "lets use the cross to decide where
          // the ball will go"). Points a charging/releasing strike down the
          // camera's forward ray — the one the screen-centre reticle marks. AFTER
          // the collection assist on purpose: the assist's "face the approach" is
          // for a LOOSE ball, which is by definition not a strike, so the two can
          // never both want the tick. See game/crosshairAim.ts for why aimYaw was
          // NOT already this number.
          applyCrosshairAim(cmd, crosshairYaw, input.state.isShootHeld() || input.state.isPassHeld());
          // §2 pass-to-highlight: on the pass release, point the strike aim AT the
          // highlighted receiver so the sim's own choosePassTarget picks the SAME
          // man the white marker showed — the ball goes where you were shown, in
          // solo and (aimYaw is on the wire) online. Aim assist, not homing: the
          // sim still leads/validates and defenders can intercept.
          if ((cmd.flags & InputFlags.Pass) !== 0 && lastPassTarget >= 0 && p.active[lastPassTarget] === 1) {
            const rx = p.px[lastPassTarget]! - p.px[controlledId]!;
            const rz = p.pz[lastPassTarget]! - p.pz[controlledId]!;
            if (rx * rx + rz * rz > 1e-6) cmd.aimYaw = Math.atan2(rz, rx);
          }
          // §5 middle-mouse team commands. The window resolved inside
          // buildInputCommand: show the HUD feedback, and if a SINGLE-click
          // CIRCULATE landed while WE are carrying, play a safe retain pass now to
          // the best available teammate (the sim's own pass path leads it to his
          // control foot, §4). Double-click pass-to-me rides the wire as
          // CallForPass, so it is already server-authoritative — no extra work.
          const mmFeedback = input.state.consumeMiddleFeedback();
          if (mmFeedback) banners.pushText(mmFeedback);
          if (input.state.consumeCirculate() && ctx.possessing) {
            const mate = choosePassTarget(world, controlledId, cmd.aimYaw);
            if (mate >= 0 && p.active[mate] === 1) {
              cmd.flags |= InputFlags.Pass;
              cmd.passCharge = CIRCULATE_PASS_CHARGE;
              const rx = p.px[mate]! - p.px[controlledId]!;
              const rz = p.pz[mate]! - p.pz[controlledId]!;
              if (rx * rx + rz * rz > 1e-6) cmd.aimYaw = Math.atan2(rz, rx);
            }
          }
          inputs.fill(null);
          inputs[controlledId] = cmd;
          // AI fills every remaining outfield slot on both teams; GK slots stay
          // null so the sim's keeper AI runs them (§4.5, §10.1).
          aiA.produceInputs(world, inputs);
          aiB.produceInputs(world, inputs);
          // §Restart no-teleport: mark THIS client's player human so an in-play
          // restart never snaps them — they walk to their waypoint (§1). Only the
          // controlled slot; it can change on a switch, so rebuild it each tick.
          humanMask.fill(0);
          humanMask[controlledId] = 1;
          step(world, inputs, stepOut, physics, humanMask);
          // Append-only handoff — banners draw on their own timer (hud.ts rule).
          if (stepOut.events.length > 0) {
            banners.pushEvents(stepOut.events);
            // §5.6 THE VISIBLE REFEREE. The sim's WHISTLE (rules/fsm.ts, raised
            // on the restart Setup→Ready flip — owner: "the ball is in game only
            // after refree whisely … players has to wait for the whistle") and
            // the card a foul severity earns drive render/referee.ts through
            // RenderState.refSignal. Per TICK, not per frame, for the same
            // reason banners/audio are: stepOut.events is zeroed at the top of
            // the next step().
            feedRefSignals(stepOut.events, renderer);
            // Goal-celebration instant replay (game/replay.ts) — start playback
            // of the buffered build-up. Guarded on `!replay.playing` so a goal
            // can never re-trigger mid-replay (there isn't one — the dwell is
            // one GOAL per celebration — but multi-tick frames make "which
            // tick's events am I looking at" worth being explicit about).
            if (!replay.playing) {
              for (const e of stepOut.events) {
                if (e.type === 'GOAL') {
                  /*
                   * Confetti over the bowl, in the SCORER's kit colours, plus
                   * fireworks after dark (render/celebration.ts). Fired from
                   * the rule event rather than a phase watch so it lands on the
                   * tick the ball crossed, not a frame or two later.
                   */
                  const kit = deriveKit(e.team);
                  const night = MATCH_ENVIRONMENT.timeOfDay === 'night';
                  renderer.celebrate(e.x ?? world.ball.px, e.z ?? world.ball.pz, kit.primary, kit.secondary, night);
                  // Crowd roar (render/stadium.ts §6.6) — spikes the target now,
                  // decays back to ambient via the countdown above once the sim's
                  // own Goal-phase dwell ends.
                  renderer.setCrowdExcitement(1);
                  crowdHypeRemainingS = GOAL_CELEBRATION_S;
                  // The GOAL wipe — a club-colour band + italic GOAL! sweep
                  // (ui/matchTransitions.ts); self-removing after 1.4 s.
                  playGoalWipe(uiEl, kitCss(kit.primary));
                  statsTracker.onGoal(
                    (e as { player?: number }).player === controlledId, e.team === 0);
                  const durationS = replay.beginPlayback(REPLAY_WINDOW_S, REPLAY_PLAYBACK_SPEED);
                  if (durationS > 0) {
                    replayGoalX = e.x ?? world.ball.px;
                    replayGoalZ = e.z ?? world.ball.pz;
                    replayElapsedS = 0;
                    replayDurationS = durationS;
                    replayChip.setVisible(true);
                  }
                  break;
                }
                // Near-miss "ooooh" — the ball crossed the goal line WITHOUT a
                // goal (corner or goal kick coming). A sharp localized surge at
                // that end, well under the roar's 1.0 so the two never read as
                // the same event. Decays via the crowd's own target decay.
                if (e.type === 'BALL_OUT_GOAL_LINE') {
                  renderer.exciteCrowdAt(world.ball.px, world.ball.pz, 0.7);
                }
              }
            }
          }
          // §11 procedural audio — fed per TICK (not per rendered frame): events
          // are drained at the top of the next step() call, so reading them only
          // after driver.advance returns would lose every tick but the last one
          // in a frame that ran more than one (same reasoning as banners above).
          audioInput.ballX = world.ball.px; audioInput.ballY = world.ball.py; audioInput.ballZ = world.ball.pz;
          audioInput.ballVX = world.ball.vx; audioInput.ballVY = world.ball.vy; audioInput.ballVZ = world.ball.vz;
          audioInput.phase = world.match.phase as MatchPhase;
          audio.update(audioInput, stepOut.events);
        });

        if (isMatchOver(world) && !ended) {
          ended = true;
          input.releasePointerLock();
          // Broadcast full-time overlay (ui/postMatch.ts) with the ego stats:
          // the run folds into the persistent career, records star gold, and
          // the praise picker guarantees the player never leaves empty-handed.
          const run = statsTracker.stats;
          const { career, broke } = recordMatch(localStoragePbStore(), run);
          fullTimeBoard = mountPostMatch(uiEl, {
            homeName: teamAName,
            awayName: teamBName,
            homeScore: world.match.scoreA,
            awayScore: world.match.scoreB,
            homeColor: kitCss(deriveKit(0).primary),
            awayColor: kitCss(deriveKit(1).primary),
            kicker: 'FULL TIME',
            statRows: [
              ['DISTANCE', run.distanceM >= 1000 ? `${(run.distanceM / 1000).toFixed(1)} km` : `${Math.round(run.distanceM)} m`],
              ['TOP SPEED', `${(run.topSpeedMs * 3.6).toFixed(1)} km/h`],
              ['SPRINTS', `${Math.round(run.sprintS)} s`],
            ],
            headlines: pickHeadlines(run, career, broke),
          }, { onRematch: rematch, onMenu: quitToMenu });
        }
      }
    }

    // ── Draw (always — a paused match still presents its frozen frame) ──────
    renderer.beginFrame();
    const b = world.ball;
    if (replay.playing) {
      // PURE RENDER: sample the buffered ring instead of WorldState. The sim
      // keeps stepping underneath (driver.advance above never stopped) — we
      // simply don't draw its output while a playback is on screen. A real
      // pause (§ header, "Pause is a REAL freeze") freezes the replay's own
      // clock too — advance()/the handback checks are skipped, but the
      // current sample keeps rendering, same as the live path's frozen frame.
      if (!paused) {
        replay.advance(dtS); // may itself end playback (buffer-end handback)
        replayElapsedS += dtS;
      }
      replay.sample(replayPlayers, replayBall);
      for (let i = 0; i < MAX_PLAYERS; i++) {
        const rp = replayPlayers[i]!;
        if (!rp.active) continue;
        renderer.setPlayer(i, rp.x, rp.y, rp.z, rp.yaw, rp.team, rp.anim, rp.animExec);
      }
      renderer.setBall(replayBall.x, replayBall.y, replayBall.z, replayBall.qx, replayBall.qy, replayBall.qz, replayBall.qw);
      if (!paused) {
        // Hand back the instant the sim's Goal dwell ends — never later, so
        // the cut hides inside the Kickoff staging that follows (the brief).
        // The buffer-end check is the other, self-contained way playback can
        // stop (a very short/aborted dwell), already applied inside advance().
        if (replay.playing && world.match.phase !== MatchPhase.Goal) replay.endPlayback();
        if (!replay.playing) replayChip.setVisible(false);
      }
    } else {
      for (let i = 0; i < MAX_PLAYERS; i++) {
        const rp = replayPlayers[i]!;
        if (p.active[i] !== 1) {
          rp.active = false;
          continue;
        }
        // Carry the sim's AnimState (D9 seam) — without it every player renders
        // Idle forever no matter how fast they run, because the player subsystem
        // has no other way to know a pass/tackle/jump happened. stepAnim() has
        // always computed this correctly; both draw loops just dropped it.
        const smp = interp.player(world, i, driver.alpha);
        renderer.setPlayer(i, smp.x, smp.y, smp.z, smp.yaw, p.team[i]!, p.anim[i]!, p.animExecId[i]!, p.vx[i]!, p.vz[i]!);
        rp.active = true;
        rp.x = p.px[i]!; rp.y = p.py[i]!; rp.z = p.pz[i]!;
        rp.yaw = p.yaw[i]!; rp.team = p.team[i]!; rp.anim = p.anim[i]!;
      }
      const bsmp = interp.ball(world, driver.alpha);
      renderer.setBall(bsmp.x, bsmp.y, bsmp.z, b.qx, b.qy, b.qz, b.qw, b.powerKick, b.possessedBy);
      // Whose hands it is in, if anyone's (§4.5, Law 15). Position above is
      // still the sim's and only the sim's; this says who to parent the drawn
      // mesh to. Fed only on the LIVE branch — a replay has no world to derive
      // it from, and `beginFrame` has already defaulted it to loose.
      const hold = resolveBallHold(world);
      renderer.setBallHold(hold.slot, hold.pose);
      replayBall.x = b.px; replayBall.y = b.py; replayBall.z = b.pz;
      replayBall.qx = b.qx; replayBall.qy = b.qy; replayBall.qz = b.qz; replayBall.qw = b.qw;
      // Ring buffer record (game/replay.ts) — exactly what was just fed to the
      // renderer above, no more (§ brief). No-op automatically while playing.
      replay.record(dtS, replayPlayers, replayBall);
    }

    // Camera target: the controlled player, eased for SWITCH_CAM_EASE_MS after
    // a takeover so the cut reads as a pan, not a teleport.
    // The SAME interpolated sample the body is drawn at — a ring or a camera on
    // the raw tick position judders against an interpolated body.
    const csmp = interp.player(world, controlledId, driver.alpha);
    const cpx = csmp.x;
    const cpy = csmp.y;
    const cpz = csmp.z;
    if (easeT < 1) {
      easeT = Math.min(1, easeT + dtMs / SWITCH_CAM_EASE_MS);
      const k = smoothstep01(easeT);
      camTarget.x = easeFromX + (cpx - easeFromX) * k;
      camTarget.y = easeFromY + (cpy - easeFromY) * k;
      camTarget.z = easeFromZ + (cpz - easeFromZ) * k;
    } else {
      camTarget.x = cpx;
      camTarget.y = cpy;
      camTarget.z = cpz;
    }

    if (!paused) {
      camInput.ballX = b.px; camInput.ballY = b.py; camInput.ballZ = b.pz;
      camInput.ballVX = b.vx; camInput.ballVZ = b.vz;
      camInput.playerX = camTarget.x; camInput.playerY = camTarget.y; camInput.playerZ = camTarget.z;
      // Camera orientation follows its OWN mouse-driven look yaw (§1/§2), which
      // WASD can never touch — feeding the aim/body yaw here is what dragged the
      // camera behind the run and stopped the mouse holding a look. Seeded from
      // the body facing so the orbit starts behind the player. (Never the ball,
      // its prediction, or the pass target — those are not camera inputs.)
      if (!input.state.cameraLookInit) input.state.seedCameraLook(p.yaw[controlledId]!);
      if (!input.state.moveHeadingInit) input.state.seedMoveHeading(p.yaw[controlledId]!);
      camInput.playerYaw = input.state.cameraLookYaw;
      camInput.ballFocus = settings.camera.ballFocus; // §10 opt-in ball framing
      camInput.playerSpeed = Math.hypot(p.vx[controlledId]!, p.vz[controlledId]!);
      camInput.sprinting = (p.anim[controlledId]! as number) === 4; // AnimState.Sprint
      camInput.pitchHalfLength = config.pitchLength * 0.5;
      camInput.pitchHalfWidth = config.pitchWidth * 0.5;
      camInput.elapsedS = elapsedS;
      // The rig always updates (warming its smoothing toward its own target);
      // during the intro the flyby pose is applied instead and converges onto
      // the rig's pose in its final stretch — seamless handover by design.
      const pose = camera.update(camInput, dtS);
      // THE AIM, refreshed from the pose the player is actually looking down —
      // taken from the LIVE rig only (see crosshairYaw's declaration: the replay
      // orbit and the intro flyby must never steer a strike).
      //
      // NaN in a camera the mouse cannot turn (Broadcast/Tele/Dynamic are fixed
      // touchline rigs — see cameraAimsWithCrosshair). That single NaN disables
      // the whole feature coherently: applyCrosshairAim no-ops, the pass-target
      // preview falls back to aimYaw, and the reticle hides, because a
      // screen-centre cross that cannot steer must not pretend to aim.
      crosshairYaw = cameraAimsWithCrosshair(camera.mode) ? crosshairAimYaw(pose) : Number.NaN;
      if (replay.playing) {
        // Scripted orbit around the goal that was scored in (game/replay.ts).
        // The live rig above still updated (still warm for an instant resume
        // the frame this ends) — we just apply the replay's pose instead.
        computeReplayCameraPose(
          replayElapsedS, replayDurationS, replayGoalX, replayGoalZ,
          world.config.pitchLength, world.config.pitchWidth,
          // The live rig kept updating above; hand it over so the replay can
          // converge back onto it instead of cutting.
          pose, replayPose,
        );
        renderer.applyCamera(replayPose);
        renderer.setBallArrow(false, cpx, cpy, cpz, 0);
      } else if (intro !== null && intro.active) {
        intro.update(dtS, pose, introPose);
        renderer.applyCamera(introPose);
        renderer.setBallArrow(false, cpx, cpy, cpz, 0);
      } else {
        renderer.applyCamera(pose);
        // A6 §8: an on-pitch direction arrow may appear ONLY with controlled
        // possession. The proximity-gated ball locator showed precisely when
        // the ball was loose / in flight / at another player — reading as a
        // possession arrow you don't have — so it no longer renders. The
        // camera pose still computes the policy value (camera.test.ts pins
        // it); only the HUD stops drawing it.
        renderer.setBallArrow(false, cpx, cpy, cpz, pose.ballArrowYaw);
      }
    }
    if (replay.playing) {
      // The controlled-player highlight ring is a LIVE-position cue; showing
      // it over historical replay footage would point at the wrong spot.
      renderer.hideControlled();
    } else {
      renderer.setControlled(cpx, cpy, cpz, easeT, elapsedS, input.state.aimYaw);
      // Boost aura on the same live position, fed from the local input's latch
      // — but that latch is client-only UI state with no idea what stamina is
      // left; the sim silently stops granting real speed at 0 while the tier
      // stays armed until the player taps it off. Owner, 2026-08-14: "when
      // stamina is 0 dont show those animations of boost 1 and 2" — gated on
      // stamina here so the embers/lightning turn off the instant the bar
      // empties. Not fed on the replay branch, for the same reason the ring is
      // not: it is a live-state cue and `beginFrame` defaults it off.
      renderer.setBoostAura(controlledId, cpx, cpy, cpz, p.stamina[controlledId]! > 0 ? input.state.boostTier : 0);
    }

    // ── Pass-target indicator (§4.2, 3C) — the SAME shared scorer the sim
    // strikes with, so the faint disc shows exactly who the pass would find.
    // Shown only while a pass is charging; hidden the frame it stops.
    //
    // GATED ON POSSESSION (owner playtest: "show pass assist only when player
    // has the ball"). Holding pass off the ball used to highlight a receiver
    // for a pass that cannot be made. Same §6 rule the kick UI gates on.
    const hasBall = possessionArrowVisible(world, controlledId);
    hud.passTargetId = -1; // radar seam: reset each frame, set below if a candidate exists
    if (input.state.isPassHeld() && hasBall) {
      // Thread the last highlight so the white marker holds steady under a small
      // aim wobble (§13 hysteresis) instead of flickering between near-tie mates.
      // Scored along the CROSSHAIR heading — the same number the strike now uses
      // (applyCrosshairAim above), so the man the ring highlights is the man the
      // pass reaches. Feeding the old `input.state.aimYaw` here would recreate
      // exactly the highlight≠strike mismatch commit fb390d9 fixed, from the
      // other side. Falls back to aimYaw before the first pose exists.
      const passAimYaw = Number.isFinite(crosshairYaw) ? crosshairYaw : input.state.aimYaw;
      const target = choosePassTarget(world, controlledId, passAimYaw, lastPassTarget);
      lastPassTarget = target;
      if (target >= 0) {
        renderer.setPassTarget(
          world.players.px[target]!,
          world.players.py[target]!,
          world.players.pz[target]!,
          target,
        );
        hud.passTargetId = target; // radar seam: same target the sim would strike to
      }
    } else {
      lastPassTarget = -1; // not charging → next pass selects fresh
    }

    // ── Charge read (LIVE FOOTBALL POLISH §3) — REQUESTED power (charge
    // fraction), never ball speed. Shared by the HUD power meter below and
    // the aim-arrow's charge-intensity feed; at most one of shoot/pass can be
    // held at once (see InputState's charge model), so this is unambiguous.
    let chargeKind: 'shoot' | 'pass' | null = null;
    let chargeFraction = 0;
    if (input.state.isShootHeld()) {
      chargeKind = 'shoot';
      chargeFraction = input.chargeFraction('shoot', SHOT_CHARGE_MIN_S, SHOT_CHARGE_MAX_S);
    } else if (input.state.isPassHeld()) {
      chargeKind = 'pass';
      chargeFraction = input.chargeFraction('pass', PASS_CHARGE_MIN_S, PASS_CHARGE_MAX_S);
    }

    // ── Kick UI gates (§4/§5) — TWO gates now, and the split is the point.
    //
    // `matchLive` is a DISPLAY gate: is this a live match the player is looking
    // at, rather than a pause menu, full-time board, kickoff intro or goal
    // replay. The screen-centre reticle rides this one, because the owner asked
    // for it to be *"always on the screen"* — it no longer disappears when you
    // lose the ball. What it still must not do is float over a pause menu or a
    // replay, which is what these four exclusions are for.
    //
    // `canShowKickUi` is the POSSESSION gate: does the controlled player truly
    // own the ball in live play (§6 shared rule — never a loose, opponent-held,
    // in-flight or keeper-held ball). The power meter needs it, and so does the
    // reticle's ARMED state and its ball-destination dot: the reticle may be
    // present without the ball, but it may not claim a destination.
    //
    // The FEET ARROW that used to ride `canShowKickUi` is gone (owner: *"dont
    // show the arrows near his foot"*) — `render/indicators.ts` no longer draws
    // it or the 7 m pitch marker that shared its gate, so there is nothing left
    // to feed. `Renderer.setAimArrow` still exists, unfed; see indicators.ts.
    const interrupted = paused || ended || introActive || replay.playing;
    const canShowKickUi = hasBall && !interrupted;

    // ── HUD (it redraws on its own timers — we only write numbers) ──────────
    hud.scoreA = world.match.scoreA;
    // In-world LED scoreboards (stadium) — same source as the HUD.
    renderer.setScoreboard(hud.nameA, hud.nameB, world.match.scoreA, world.match.scoreB,
      world.match.clockTicks / 60,
      (world.match.phase as number) === (MatchPhase.HalfTime as number) ? 'HT'
        : (world.match.phase as number) === (MatchPhase.FullTime as number) ? 'FT'
        : world.match.half >= 1 ? '2ND' : '1ST');
    hud.scoreB = world.match.scoreB;
    hud.clockTicks = world.match.clockTicks;
    hud.addedTimeTicks = world.match.addedTimeTicks;
    hud.half = world.match.half;
    hud.phase = world.match.phase as MatchPhase;
    // Restart caption seam (ui/banners.ts takerSub): who has been designated to
    // take the throw-in / corner / goal kick that is currently being staged.
    hud.restartTaker = world.match.restartTaker;
    // The projected corner delivery — computed from the sim's own ballistics
    // (ai/restart-preview.ts), or not drawn at all. Never a fixed distance.
    if (previewCornerDelivery(world, cornerDelivery)) {
      const f = cornerDelivery.flight;
      renderer.setCornerTrail(f.points, f.count, f.landX, f.landZ, f.landed, cornerDelivery.exact);
    }
    hud.localPlayerId = controlledId;
    hud.teamAAttacksPositiveX = world.teamAAttacksPositiveX; // radar seam: mirroring input
    hud.rttMs = -1; // offline: hide the ping readout
    hud.fps = fps;
    hud.ballX = b.px;
    hud.ballZ = b.pz;
    // Feed the always-on stamina bar from the man the player is actually
    // controlling (owner: "show the stamina and power bars on the screen").
    // STAMINA_MAX normalises it; the boost tier drives the bar's colour so
    // spending is legible without a second readout.
    hud.stamina = world.players.stamina[controlledId]! / 100;
    // The sim keeps no per-player boost tier, so read the INTENT the local
    // player is sending this frame - which is what the bar's colour is about
    // (am I spending?) rather than a simulated state.
    hud.boostTier = input.state.boostTier;
    hud.chargeShow = canShowKickUi && chargeKind !== null;
    if (chargeKind !== null) hud.chargeKind = chargeKind;
    hud.chargeFraction = chargeFraction;
    // Screen-centre aiming crosshair — ALWAYS ON while the match is live
    // (owner: *"keep the cross air always on the screen"*). It was gated on
    // `canShowKickUi`, i.e. it blinked out every time the ball left your feet.
    // Now only `matchLive` (pause / full-time / intro / goal replay still hide
    // it) plus the player's own Settings > HUD toggle. Possession moved to
    // `crosshairArmed`, which drives the dim/full look and the destination dot —
    // present without the ball, but not pretending you can shoot one.
    // `!aimable` joins the hide-cases: in a fixed-azimuth camera the reticle
    // would sit over the play marking a direction it cannot change (see
    // cameraAimsWithCrosshair). "Always on the screen" means "does not blink out
    // when you lose the ball" — not "drawn where it would be a lie".
    const aimable = Number.isFinite(crosshairYaw);
    hud.crosshairShow = crosshairShown(settings.crosshairEnabled, interrupted || !aimable);
    hud.crosshairArmed =
      crosshairArmed(settings.crosshairEnabled, interrupted || !aimable, canShowKickUi);
    hud.crosshairSize = settings.crosshairSize;
    for (let i = 0; i < MAX_PLAYERS; i++) {
      hud.px[i] = p.px[i]!;
      hud.pz[i] = p.pz[i]!;
      hud.active[i] = p.active[i]!;
      hud.team[i] = p.team[i]!;
      hud.yaw[i] = p.yaw[i]!; // radar seam: controlled-player facing arrow
    }

    renderer.render();
  };

  // §11 audio scratch — hoisted, zero per-tick allocation (§15.7).
  const audioInput: AudioTickInput = {
    dt: FIXED_DT,
    ballX: 0, ballY: 0, ballZ: 0,
    ballVX: 0, ballVY: 0, ballVZ: 0,
    phase: MatchPhase.Warmup,
    pitchHalfLength: config.pitchLength * 0.5,
  };

  // Camera scratch — hoisted, zero per-frame allocation (§15.7).
  const camTarget = { x: 0, y: 0, z: 0 };
  const camInput: CameraInput = {
    ballX: 0, ballY: 0, ballZ: 0, ballVX: 0, ballVZ: 0,
    playerX: 0, playerY: 0, playerZ: 0, playerYaw: 0,
    playerSpeed: 0, sprinting: false,
    pitchHalfLength: config.pitchLength * 0.5,
    pitchHalfWidth: config.pitchWidth * 0.5,
    elapsedS: 0,
  };

  // Replay camera scratch — hoisted, zero per-frame allocation (§15.7).
  const replayPose: CameraPose = {
    x: 0, y: 0, z: 0, lookX: 0, lookY: 0, lookZ: 0, fov: 60,
    showBallArrow: false, ballArrowYaw: 0, offBallBlend: 0,
  };

  const dispose = (): void => {
    if (disposed) return;
    disposed = true;
    intro?.dispose();
    intro = null;
    clearCastOverlay();
    pauseOverlay?.dispose();
    fullTimeBoard?.dispose();
    doors?.dispose();
    settingsScreen?.dispose();
    banners.dispose();
    audio.dispose();
    replayChip.dispose();
    window.removeEventListener('keydown', onReplaySkipKey, { capture: true });
    window.removeEventListener('mousedown', onReplaySkipClick, { capture: true });
    hudHandle.dispose();
    input.releasePointerLock();
    input.detach();
    sceneEl.removeEventListener('click', onSceneClick);
    document.removeEventListener('pointerlockchange', onPointerLockChange);
    window.removeEventListener('resize', onResize);
    hiddenPump?.stop();
    renderer.dispose();
    physics.dispose();
  };

  // Dev handle on the SIM (renderer.ts already exposes `__fg` for the scene).
  // Live QA needs the authoritative world — ball velocity, possession, anim
  // state — and deriving those from rendered positions is guesswork that has
  // already produced one wrong diagnosis ("camera is 36m from the player" is
  // meaningless for a camera that sits 25m behind its own target). Dev builds
  // only; `import.meta.env.DEV` strips it from a production bundle.
  if (import.meta.env.DEV) {
    (window as unknown as Record<string, unknown>)['__fgSim'] = {
      world,
      get controlledId() {
        return controlledId;
      },
      config,
    };
  }

  console.info(
    `[fg] practice: ${setup.squad}v${setup.squad} vs COM (${difficulty}), ` +
      `${setup.halfMinutes}min halves, ${setup.timeOfDay}/${setup.weather}, seed=${config.seed}, offline`,
  );
  scheduleFrame();
  // DEV/QA only: keep this loop running in real time while the tab is hidden.
  // Without it a headless match runs at ~1 fps and nothing that only happens in
  // real time can be verified at all. See `hidden-tab-pump.ts`.
  const hiddenPump = import.meta.env.DEV ? startHiddenTabPump(frame) : null;

  return { dispose };
}
